import React from 'react';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import relationships from 'Consts/Relationships';
import PropTypes from 'prop-types';
import { decodeQuery, currentSearch } from 'Helpers/Query';
import useCollection from 'Hooks/useCollection';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { householdSecureFilesPreviewUri } from 'Consts/sharedUris';
import { useHistory } from 'react-router';

const MODAL_PATH_QUERY_ACCOUNT = 'modal=account';

const MARK_COMPLETE_MS_TIMEOUT = 10000;


function ListItemContainer({
  resource, children: Child, refreshActionItems, replaceActionItem,
}) {
  const { householdResource } = useSimpleHousehold();

  const [loading, setLoading] = React.useState(true);
  const {
    collection: fileLinkCollection,
    setCollection: setFileLinkCollection,
  } = useCollection();
  const [uploadProgress, setUploadProgress] = React.useState(null);
  const [expand, setExpand] = React.useState(false);
  const [fileExpand, setFileExpand] = React.useState(false);
  const [showMarkCompleteTooltip, setShowMarkCompleteTooltip] = React.useState(false);
  const [previousLocationSearch, setPreviousLocationSearch] = React.useState('');
  const [hasAccounts, setHasAccounts] = React.useState(null);
  const [selectedFileId, setSelectedFileId] = React.useState('');
  const [selectedAccountId, setSelectedAccountId] = React.useState('');
  const [alternativeToggleDisplay, setAlternativeToggleDisplay] = React.useState(false);

  const timeoutRef = React.useRef();

  const history = useHistory();

  const hidden = resource.attr('hidden') === 'true';
  const status = resource.attr('status');
  const name = resource.attr('name');
  const completed = resource.attr('status') === 'completed';

  function setProgress(progress) {
    if (progress === null) {
      setUploadProgress(null);
    } else {
      const diff = Math.random() * 10;
      setUploadProgress(Math.min(progress + diff, 100));

      if (progress + diff >= 100 && status !== 'complete') {
        setShowMarkCompleteTooltip(true);
      }
    }
  }

  function handleDeleteFile(file) {
    if (file.id() === selectedFileId) {
      setSelectedFileId('');
    }

    return file
      .link('self')
      .Delete()
      .then(() => fileLinkCollection.refresh())
      .then(coll => setFileLinkCollection(coll))
      .then(() => displaySuccess('Successfully removed file'))
      .catch((e) => {
        const msg = e.response?.body?.errors ? e.response?.body?.errors : 'Failed to delete file';
        displayErrorMessage(e, msg);
      });
  }

  function handleFileClick(file) {
    const currentQueries = decodeQuery(currentSearch());
    history.push(
      householdSecureFilesPreviewUri(
        householdResource.id(),
        [file.id()],
        currentQueries,
      ),
    );

    return null;
  }

  const toggleHide = () => {
    const updateResource = resource.copy().attr('hidden', hidden ? 'false' : 'true');
    updateResource
      .submit({
        linkArgs: {
          raw: true,
        },
      })
      .then(() => refreshActionItems())
      .then(() => displaySuccess(`${name} has been hidden!`))
      .catch(e => displayErrorMessage(e, e.errors || `failed to hide ${name}`));
  };

  const markAsComplete = () => {
    if (showMarkCompleteTooltip) setShowMarkCompleteTooltip(false);

    const updateResource = resource.copy().attr('status', 'completed');

    updateResource
      .submit({
        linkArgs: {
          raw: true,
        },
      })
      .then(() => replaceActionItem(updateResource)) // TODO: fr now tries to return a collection by default this doesn't work well with the action item put endpoint
      .then(() => displaySuccess(`${name} has been completed!`))
      .catch(e => displayErrorMessage(e, e.errors || `failed to mark ${name} complete`));
  };

  async function refreshHasAccounts() {
    if (!householdResource) {
      return Promise.resolve();
    }
    const newAccountCollection = await householdResource.relationship(relationships.accounts).link('related').get();
    setHasAccounts(newAccountCollection.resources().list().length > 0);
    return Promise.resolve();
  }

  React.useEffect(() => {
    if (resource) {
      const link = resource.relationship('getSecureFiles')?.link('related');

      if (link) {
        link.Get().then((res) => {
          setFileLinkCollection(res);
        });
      }
    }
  }, [resource]);

  React.useEffect(() => {
    if (showMarkCompleteTooltip) {
      timeoutRef.current = setTimeout(() => {
        setShowMarkCompleteTooltip(false);
      }, MARK_COMPLETE_MS_TIMEOUT);
    }

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [showMarkCompleteTooltip]);
  React.useEffect(() => {
    refreshHasAccounts().catch(e => displayErrorMessage(e, e.errors));
  }, [householdResource]);
  React.useEffect(() => {
    //  When we close the account modal, check to see if we now have accounts.
    if (previousLocationSearch.includes(MODAL_PATH_QUERY_ACCOUNT) && !history.location.search.includes(MODAL_PATH_QUERY_ACCOUNT)) {
      refreshHasAccounts().catch(e => displayErrorMessage(e, e.errors));
    }
    setPreviousLocationSearch(history.location.search);
  }, [history.location]);

  return (
    <Child
      loading={loading}
      uploadProgress={uploadProgress}
      setProgress={setProgress}
      setFileLinkCollection={setFileLinkCollection}
      fileLinkCollection={fileLinkCollection}
      refreshHasAccounts={refreshHasAccounts}
      markAsComplete={markAsComplete}
      handleFileClick={handleFileClick}
      handleDeleteFile={handleDeleteFile}
      setLoading={setLoading}
      expand={expand}
      setExpand={setExpand}
      setFileExpand={setFileExpand}
      fileExpand={fileExpand}
      hasAccounts={hasAccounts}
      completed={completed}
      selectedFileId={selectedFileId}
      setSelectedFileId={setSelectedFileId}
      selectedAccountId={selectedAccountId}
      setSelectedAccountId={setSelectedAccountId}
      toggleHide={toggleHide}
      showMarkCompleteTooltip={showMarkCompleteTooltip}
      alternativeToggleDisplay={alternativeToggleDisplay}
      setAlternativeToggleDisplay={setAlternativeToggleDisplay}
    />);
}

ListItemContainer.propTypes = {
  replaceActionItem: PropTypes.func.isRequired,
  refreshActionItems: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  resource: PropTypes.object.isRequired,
};

export default ListItemContainer;
